/**
 * Whether a menu link reaches a real place: a site path (`/page`), an
 * `#anchor` or `?query`, a web address that parses with a host, or another
 * scheme with something after it (`mailto:`, `tel:`, `whatsapp:`).
 *
 * Plain words like `not a url` fail. The storefront would serve them as a
 * relative path and land on a 404 (TC-65 #10). Script schemes pass this check
 * and are refused separately, by `hasDeniedUrlScheme`.
 */
export function isNavigableMenuUrl(raw: string): boolean {
  const url = raw.trim();
  if (url.length === 0 || /\s/.test(url)) return false;
  if (/^[/#?]/.test(url)) return true;
  if (/^https?:/i.test(url)) {
    return URL.canParse(url) && /^https?:\/\/[^/]/i.test(url);
  }
  if (looksLikeHostWithPort(url)) return false;
  return /^[a-z][a-z0-9+.-]*:.+/i.test(url);
}

/**
 * `example.com:8080/deals` or `localhost:3000` — a host and port with no
 * scheme. It matches the scheme pattern above, but a browser reads
 * `example.com:` as an unknown scheme, so the link goes nowhere. A real scheme
 * has no dot in it, and `localhost` is not one.
 */
export function looksLikeHostWithPort(url: string): boolean {
  return /^(localhost|[a-z0-9-]+(\.[a-z0-9-]+)+):\d+(?:[/?#]|$)/i.test(url);
}

export const INVALID_MENU_URL_MESSAGE =
  "Enter a valid URL: a web address (https://…), a page path (/page), an #anchor, an email (mailto:) or a phone number (tel:).";
